import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api, { classesAPI, paymentsAPI } from '../lib/api';
import { formatStripeError } from '../lib/stripeErrors';
import { InAppBrowserWarning } from '../components/InAppBrowserWarning';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast, Toaster } from 'sonner';
import { 
  Sparkles, CheckCircle, CreditCard, Calendar, Clock, MapPin, 
  User, ArrowRight, Shield, Lock, ChevronDown, AlertCircle, Mail, Phone,
  Heart, ArrowLeft, UserPlus
} from 'lucide-react';
import Navbar from '../components/Navbar';
import { formatPrice, formatTime } from '../lib/utils';
import DateOfBirthInput from '../components/DateOfBirthInput';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1f2937',
      '::placeholder': { color: '#9ca3af' },
      iconColor: '#9333ea',
    },
    invalid: { color: '#ef4444', iconColor: '#ef4444' },
  },
};

// Step indicator component
const StepIndicator = ({ currentStep, steps }) => (
  <div className="flex items-center justify-center mb-8">
    {steps.map((step, index) => (
      <div key={index} className="flex items-center">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
          index + 1 < currentStep 
            ? 'bg-emerald-500 text-white'
            : index + 1 === currentStep
              ? 'bg-purple-600 text-white'
              : 'bg-neutral-200 text-neutral-500'
        }`}>
          {index + 1 < currentStep ? <CheckCircle className="w-5 h-5" /> : index + 1}
        </div>
        <span className={`ml-2 text-sm hidden sm:block ${
          index + 1 === currentStep ? 'text-purple-600 font-medium' : 'text-neutral-500'
        }`}>
          {step}
        </span>
        {index < steps.length - 1 && (
          <div className={`w-8 sm:w-16 h-0.5 mx-2 ${
            index + 1 < currentStep ? 'bg-emerald-500' : 'bg-neutral-200'
          }`} />
        )}
      </div>
    ))}
  </div>
);

// Card Form Component
const CardForm = ({ onSuccess, pendingId, studentName }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    initSetupIntent();
  }, []);

  const initSetupIntent = async () => {
    try {
      const res = await api.post('/payments/guest-card-setup', {
        pending_id: pendingId,
        student_name: studentName
      });
      setClientSecret(res.data.client_secret);
    } catch (err) {
      console.error('Failed to init setup intent:', err);
      setError('Failed to initialize card setup. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    setLoading(true);
    setError(null);

    try {
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (stripeError) {
        setError(formatStripeError(stripeError));
        setLoading(false);
        return;
      }

      if (setupIntent.status === 'succeeded') {
        const confirmRes = await api.post('/payments/confirm-guest-card', {
          pending_id: pendingId,
          payment_method_id: setupIntent.payment_method
        });
        toast.success('Card saved successfully!');
        onSuccess(confirmRes.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save card');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <InAppBrowserWarning />
      <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200">
        <CardElement options={cardElementOptions} />
      </div>
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}
      <Button
        type="submit"
        disabled={!stripe || !clientSecret || loading}
        className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 h-12"
        data-testid="save-card-button"
      >
        {loading ? 'Saving...' : 'Save Card & Continue'}
      </Button>
      <p className="text-xs text-neutral-500 text-center flex items-center justify-center gap-1">
        <Lock className="w-3 h-3" />
        Your card will only be charged after admin authorization
      </p>
    </form>
  );
};

// Main Component
const EnrollAfterTrialPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuth();
  
  // URL params
  const trialId = searchParams.get('trial');
  const preselectedClassId = searchParams.get('classId');
  const studentNameParam = searchParams.get('student');
  
  // Steps: 1=Classes, 2=Student Details, 3=Guardian, 4=Medical, 5=Card, 6=Account
  const [step, setStep] = useState(1);
  const steps = ['Classes', 'Student', 'Guardian', 'Medical', 'Card', 'Account'];
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Data
  const [trialData, setTrialData] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState(new Set());
  const [pendingId, setPendingId] = useState(null);
  const [cardSaved, setCardSaved] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Form data - Student
  const [studentForm, setStudentForm] = useState({
    name: studentNameParam || '',
    birthDate: '',
    gender: ''
  });
  
  // Form data - Guardian
  const [guardianForm, setGuardianForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    // Second guardian
    guardian2FirstName: '',
    guardian2LastName: '',
    guardian2Email: '',
    guardian2Phone: ''
  });
  
  // Form data - Medical
  const [medicalForm, setMedicalForm] = useState({
    conditions: '',
    allergies: '',
    emergencyContact: '',
    emergencyPhone: ''
  });
  
  // Form data - Account
  const [accountForm, setAccountForm] = useState({
    password: '',
    confirmPassword: ''
  });

  // Initialize
  useEffect(() => {
    if (trialId) {
      verifyTrialAccess();
    } else {
      setError('No trial ID provided. Please use the link from your email.');
      setVerifying(false);
      setLoading(false);
    }
  }, [trialId]);

  useEffect(() => {
    if (preselectedClassId && classes.length > 0) {
      setSelectedClasses(new Set([preselectedClassId]));
    }
  }, [preselectedClassId, classes]);

  const verifyTrialAccess = async () => {
    try {
      const res = await api.get(`/trials/verify-access/${trialId}`);
      setTrialData(res.data);
      
      // Pre-fill student info from trial
      setStudentForm({
        name: res.data.student_name || studentNameParam || '',
        birthDate: res.data.birth_date || '',
        gender: res.data.gender || ''
      });
      
      // Pre-fill guardian info from trial
      if (res.data.guardian) {
        setGuardianForm({
          email: res.data.guardian.email || '',
          firstName: res.data.guardian.first_name || '',
          lastName: res.data.guardian.last_name || '',
          phone: res.data.guardian.phone || '',
          guardian2FirstName: '',
          guardian2LastName: '',
          guardian2Email: '',
          guardian2Phone: ''
        });
      }
      
      // Pre-fill medical from trial
      if (res.data.medical_conditions) {
        setMedicalForm(prev => ({ ...prev, conditions: res.data.medical_conditions }));
      }
      
      // Pre-select the trial class
      if (res.data.trial_class?.id) {
        setSelectedClasses(new Set([res.data.trial_class.id]));
      }
      
      await fetchClasses();
      setVerifying(false);
      
    } catch (err) {
      console.error('Trial verification failed:', err);
      setError(err.response?.data?.detail || 'This enrollment link is invalid or has expired.');
      setVerifying(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const res = await classesAPI.getAll();
      const availableClasses = res.data.filter(c => 
        c.is_visible !== false && (c.term_price || c.subscription_price)
      );
      setClasses(availableClasses);
    } catch (error) {
      toast.error('Failed to load classes');
    }
  };

  const handleClassToggle = (classId) => {
    const newSelected = new Set(selectedClasses);
    if (newSelected.has(classId)) {
      newSelected.delete(classId);
    } else {
      newSelected.add(classId);
    }
    setSelectedClasses(newSelected);
  };

  const calculateTotal = () => {
    let total = 0;
    classes.filter(c => selectedClasses.has(c.id)).forEach(cls => {
      total += cls.term_price || cls.subscription_price || 0;
    });
    return total;
  };

  // Step navigation handlers
  const handleStep1Next = () => {
    if (selectedClasses.size === 0) {
      toast.error('Please select at least one class');
      return;
    }
    setStep(2);
  };

  const handleStep2Next = () => {
    if (!studentForm.name || !studentForm.birthDate) {
      toast.error('Please fill in student name and date of birth');
      return;
    }
    setStep(3);
  };

  const handleStep3Next = () => {
    if (!guardianForm.email || !guardianForm.firstName || !guardianForm.lastName || !guardianForm.phone) {
      toast.error('Please fill in all required guardian fields');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(guardianForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setStep(4);
  };

  const handleStep4Next = async () => {
    if (!medicalForm.emergencyContact || !medicalForm.emergencyPhone) {
      toast.error('Please provide emergency contact details');
      return;
    }
    
    // Submit enrollment to get pending ID
    setSubmitting(true);
    try {
      const res = await api.post('/trials/submit-enrollment', {
        trial_id: trialId,
        student_name: studentForm.name,
        birth_date: studentForm.birthDate,
        gender: studentForm.gender,
        guardian_email: guardianForm.email,
        guardian_first_name: guardianForm.firstName,
        guardian_last_name: guardianForm.lastName,
        guardian_phone: guardianForm.phone,
        guardian2_first_name: guardianForm.guardian2FirstName,
        guardian2_last_name: guardianForm.guardian2LastName,
        guardian2_email: guardianForm.guardian2Email,
        guardian2_phone: guardianForm.guardian2Phone,
        medical_conditions: medicalForm.conditions,
        allergies: medicalForm.allergies,
        emergency_contact: medicalForm.emergencyContact,
        emergency_phone: medicalForm.emergencyPhone,
        selected_class_ids: Array.from(selectedClasses),
        total_amount: calculateTotal()
      });
      
      setPendingId(res.data.pending_id);
      setStep(5);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit enrollment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCardSaved = (data) => {
    setCardSaved(true);
    setStep(6);
  };

  const handleCreateAccount = async () => {
    if (!accountForm.password || accountForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (accountForm.password !== accountForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      // Create account and complete registration
      const res = await api.post('/trials/complete-with-account', {
        pending_id: pendingId,
        email: guardianForm.email,
        password: accountForm.password,
        first_name: guardianForm.firstName,
        last_name: guardianForm.lastName
      });
      
      // Auto-login
      if (res.data.token) {
        setAuth(res.data.token, res.data.user);
        toast.success('Account created! Welcome to UPA!');
        navigate('/parent');
      } else {
        setSuccess(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading || verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="animate-spin w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full mx-auto" />
          <p className="mt-4 text-neutral-600">Verifying your enrollment link...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
        <Navbar />
        <div className="container mx-auto px-4 py-20 max-w-lg">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-red-700 mb-2">Unable to Access Enrollment</h2>
              <p className="text-red-600">{error}</p>
              <Button 
                className="mt-6"
                onClick={() => navigate('/contact')}
              >
                Contact UPA
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
        <Navbar />
        <div className="container mx-auto px-4 py-20 max-w-lg text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-bold text-neutral-900 mb-4">Registration Complete!</h1>
          <p className="text-neutral-600 mb-8">
            Welcome to Universal Performing Arts! Your enrollment has been submitted.
          </p>
          <Button 
            className="bg-gradient-to-r from-purple-600 to-pink-500"
            onClick={() => navigate('/parent')}
          >
            Go to Parent Portal
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      <Toaster position="top-center" />
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">
            Complete Your Enrollment
          </h1>
          <p className="text-neutral-600">
            {trialData?.student_name ? `Welcome, ${trialData.student_name}!` : 'Continue your journey with UPA'}
          </p>
        </div>

        {/* Step Indicator */}
        <StepIndicator currentStep={step} steps={steps} />

        {/* Step 1: Class Selection */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                Select Your Classes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {trialData?.trial_class && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 mb-4">
                  <p className="text-sm text-purple-700 font-medium mb-1">Your Trial Class</p>
                  <p className="text-purple-900">{trialData.trial_class.name}</p>
                </div>
              )}
              
              <div className="grid gap-3">
                {classes.map(cls => (
                  <div
                    key={cls.id}
                    onClick={() => handleClassToggle(cls.id)}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      selectedClasses.has(cls.id)
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-neutral-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-neutral-900">{cls.name}</h3>
                        <p className="text-sm text-neutral-500">Ages {cls.age_min}-{cls.age_max}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-purple-600">
                          £{(cls.term_price || cls.subscription_price || 0).toFixed(2)}
                        </p>
                        <p className="text-xs text-neutral-500">per term</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {selectedClasses.size > 0 && (
                <div className="p-4 bg-neutral-100 rounded-lg">
                  <div className="flex justify-between font-semibold">
                    <span>Total</span>
                    <span className="text-purple-600">£{calculateTotal().toFixed(2)}</span>
                  </div>
                </div>
              )}

              <Button 
                onClick={handleStep1Next}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-500 h-12"
                data-testid="step1-continue"
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Student Details */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-purple-600" />
                Student Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Student's Full Name *</Label>
                <Input
                  value={studentForm.name}
                  onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })}
                  placeholder="Enter full name"
                  data-testid="student-name"
                />
              </div>
              
              <DateOfBirthInput
                value={studentForm.birthDate}
                onChange={(val) => setStudentForm({ ...studentForm, birthDate: val })}
                label="Date of Birth *"
                required
              />
              
              <div>
                <Label>Gender</Label>
                <select
                  value={studentForm.gender}
                  onChange={(e) => setStudentForm({ ...studentForm, gender: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-neutral-300 bg-white"
                  data-testid="student-gender"
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button 
                  onClick={handleStep2Next}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-500"
                  data-testid="step2-continue"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Guardian Details */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-purple-600" />
                Guardian & Contact Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input
                    value={guardianForm.firstName}
                    onChange={(e) => setGuardianForm({ ...guardianForm, firstName: e.target.value })}
                    placeholder="First name"
                    data-testid="guardian-first-name"
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input
                    value={guardianForm.lastName}
                    onChange={(e) => setGuardianForm({ ...guardianForm, lastName: e.target.value })}
                    placeholder="Last name"
                    data-testid="guardian-last-name"
                  />
                </div>
              </div>
              
              <div>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={guardianForm.email}
                  onChange={(e) => setGuardianForm({ ...guardianForm, email: e.target.value })}
                  placeholder="email@example.com"
                  data-testid="guardian-email"
                />
              </div>
              
              <div>
                <Label>Phone Number *</Label>
                <Input
                  type="tel"
                  value={guardianForm.phone}
                  onChange={(e) => setGuardianForm({ ...guardianForm, phone: e.target.value })}
                  placeholder="07xxx xxxxxx"
                  data-testid="guardian-phone"
                />
              </div>

              {/* Second Guardian (Optional) */}
              <div className="pt-4 border-t">
                <p className="text-sm font-medium text-neutral-700 mb-3">Second Guardian (Optional)</p>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    value={guardianForm.guardian2FirstName}
                    onChange={(e) => setGuardianForm({ ...guardianForm, guardian2FirstName: e.target.value })}
                    placeholder="First name"
                  />
                  <Input
                    value={guardianForm.guardian2LastName}
                    onChange={(e) => setGuardianForm({ ...guardianForm, guardian2LastName: e.target.value })}
                    placeholder="Last name"
                  />
                </div>
                <Input
                  type="email"
                  value={guardianForm.guardian2Email}
                  onChange={(e) => setGuardianForm({ ...guardianForm, guardian2Email: e.target.value })}
                  placeholder="Email (optional)"
                  className="mt-2"
                />
                <Input
                  type="tel"
                  value={guardianForm.guardian2Phone}
                  onChange={(e) => setGuardianForm({ ...guardianForm, guardian2Phone: e.target.value })}
                  placeholder="Phone (optional)"
                  className="mt-2"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button 
                  onClick={handleStep3Next}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-500"
                  data-testid="step3-continue"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Medical Information */}
        {step === 4 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-purple-600" />
                Medical & Emergency Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Medical Conditions</Label>
                <Textarea
                  value={medicalForm.conditions}
                  onChange={(e) => setMedicalForm({ ...medicalForm, conditions: e.target.value })}
                  placeholder="List any medical conditions, disabilities, or special needs we should be aware of..."
                  rows={3}
                  data-testid="medical-conditions"
                />
              </div>
              
              <div>
                <Label>Allergies</Label>
                <Textarea
                  value={medicalForm.allergies}
                  onChange={(e) => setMedicalForm({ ...medicalForm, allergies: e.target.value })}
                  placeholder="List any allergies..."
                  rows={2}
                  data-testid="medical-allergies"
                />
              </div>

              <div className="pt-4 border-t">
                <p className="text-sm font-medium text-neutral-700 mb-3">Emergency Contact *</p>
                <div>
                  <Label>Contact Name *</Label>
                  <Input
                    value={medicalForm.emergencyContact}
                    onChange={(e) => setMedicalForm({ ...medicalForm, emergencyContact: e.target.value })}
                    placeholder="Emergency contact name"
                    data-testid="emergency-contact"
                  />
                </div>
                <div className="mt-2">
                  <Label>Contact Phone *</Label>
                  <Input
                    type="tel"
                    value={medicalForm.emergencyPhone}
                    onChange={(e) => setMedicalForm({ ...medicalForm, emergencyPhone: e.target.value })}
                    placeholder="Emergency contact phone"
                    data-testid="emergency-phone"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setStep(3)} className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button 
                  onClick={handleStep4Next}
                  disabled={submitting}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-500"
                  data-testid="step4-continue"
                >
                  {submitting ? 'Saving...' : 'Continue'} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Card Capture */}
        {step === 5 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-purple-600" />
                Payment Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 mb-4">
                <p className="text-sm text-purple-700">
                  <Shield className="w-4 h-4 inline mr-1" />
                  Your card will be securely saved. Payment will only be taken after admin authorization.
                </p>
              </div>

              <div className="p-4 bg-neutral-100 rounded-lg mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span>Classes selected</span>
                  <span>{selectedClasses.size}</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>Total</span>
                  <span className="text-purple-600">£{calculateTotal().toFixed(2)}</span>
                </div>
              </div>

              <Elements stripe={stripePromise}>
                <CardForm 
                  onSuccess={handleCardSaved}
                  pendingId={pendingId}
                  studentName={studentForm.name}
                />
              </Elements>

              <Button variant="outline" onClick={() => setStep(4)} className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 6: Account Setup */}
        {step === 6 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-purple-600" />
                Create Your Account
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 mb-4">
                <p className="text-sm text-emerald-700 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Card saved successfully! Now create your account to access the parent portal.
                </p>
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={guardianForm.email}
                  disabled
                  className="bg-neutral-100"
                />
              </div>
              
              <div>
                <Label>Create Password *</Label>
                <Input
                  type="password"
                  value={accountForm.password}
                  onChange={(e) => setAccountForm({ ...accountForm, password: e.target.value })}
                  placeholder="At least 8 characters"
                  data-testid="account-password"
                />
              </div>
              
              <div>
                <Label>Confirm Password *</Label>
                <Input
                  type="password"
                  value={accountForm.confirmPassword}
                  onChange={(e) => setAccountForm({ ...accountForm, confirmPassword: e.target.value })}
                  placeholder="Confirm your password"
                  data-testid="account-confirm-password"
                />
              </div>

              <Button 
                onClick={handleCreateAccount}
                disabled={submitting}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-500 h-12"
                data-testid="create-account-button"
              >
                {submitting ? 'Creating Account...' : 'Create Account & Access Portal'}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EnrollAfterTrialPage;
